import { Injectable, Signal, WritableSignal, signal } from '@angular/core';
import { NavigationEnd, NavigationStart, Router } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, filter, switchMap, tap } from 'rxjs';

const THEME = {
	FR_IDF: 'default',
	FR_LYON: 'lyon',
	'FR_AIX-MARSEILLE-PROVENCE': 'marseille',
} as const;

@Injectable({ providedIn: 'root' })
export class UiService {
	public header: UiHeader;
	public content: UiContent;
	private readonly _theme = signal('ripeurs-operator-app-theme-default');
	public readonly theme = this._theme.asReadonly();

	constructor(
		private readonly _router: Router,
		private readonly _translate: TranslateService,
	) {
		this.header = new UiHeader({
			toolbarTitle: null,
			toolbarShow: false,
			loaderBarShow: false,
		});
		this.content = new UiContent({ scroll: [0, 0] });

		this.header.subject.pipe(
			filter((header) => Array.isArray(header.toolbarTitle)),
			switchMap((header) => {
				const [key, interpolateParams]: any = header.toolbarTitle;
				return this._translate.get(key, interpolateParams).pipe(
					tap((toolbarTitle) => this.header.set({ toolbarTitle })),
				);
			}),
		).subscribe();

		this._router.events.pipe(
			filter((event => event instanceof NavigationStart || event instanceof NavigationEnd)),
		).subscribe((event) => {
			const isNavigationStart = event instanceof NavigationStart;

			if (isNavigationStart) this.header.set({ toolbarTitle: null });
			this.header.set({ loaderBarShow: isNavigationStart });
		});
	}

	public setTheme(value: keyof typeof THEME): void {
		if (THEME[value]) {
			this._theme.set(`ripeurs-operator-app-theme-${THEME[value]}`);
		}
	}
}

abstract class UiElement<T> {
	public readonly value: Signal<T>;
	public subject: BehaviorSubject<T>;
	private readonly _value: WritableSignal<T>;

	constructor(defaultData: T) {
		this._value = signal(defaultData);
		this.value = this._value.asReadonly();
		this.subject = new BehaviorSubject<T>(this._value());
	}

	public set(data: Partial<T>): void {
		this._value.update((current) => Object.assign({}, current, data));
		this.subject.next(this._value());
	}

	public get(): T { return this._value(); }
}

class UiHeader extends UiElement<{
	toolbarTitle?: string | [string] | [string, { [k: string]: any }] | null,
	toolbarShow?: boolean,
	loaderBarShow?: boolean,
}> {}

class UiContent extends UiElement<{
	scroll?: [number, number],
}> {}
